import React, { useEffect, useState, useContext } from "react";
import { Container, Col, Row } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState, AppDispatch } from "../../../slices/store";
import { TodosContext } from "../../../context";
import useReqRequests from "../../../utils/helpers/helperMethods/userRequets";
import UseAuthConfig from "../../../hooks/useAuthConfig";
import { allCustomersGet } from "../../../slices/clients/clientManagementSlice";
import { jobSingleGet } from "../../../slices/jobs/jobManagementSlice";
import {
  handleCreateJobService,
  handleEditJobService,
} from "../service/JobsService";
import LabelInsideInput from "../../../components/molecules/InputController/LabelInsideInputField";
import LabelInsideSelectField from "../../../components/molecules/InputController/LabelInsideSelectField";
import LabelInsideDateInputField from "../../../components/molecules/InputController/LabelInsideDateInputField";
import BreadCrumb from "../../../components/organisms/Breadcrumb/BreadCrumb";
import Headline from "../../../components/organisms/Headline/Headline";
import { jobRequests } from "../constants";

const JobForm = ({
  isEditMode,
  jobId,
}: {
  isEditMode: boolean;
  jobId?: string;
}) => {
  const { protectedResources } = UseAuthConfig();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { reqRequests } = useReqRequests();
  const { addAlert } = useContext(TodosContext);
  const selectedJob = useSelector(
    (state: RootState) => state.jobsManagement.selectedJob
  );
  const customers = useSelector(
    (state: RootState) => state.clientManagement.clients
  );

  const [isLoading, setIsLoading] = useState(false);
  const [formReady, setFormReady] = useState(!isEditMode);
  const [clients, setClients] = useState([
    { value: "", label: "Please Select", isDisabled: true },
  ]);

  const isValidDate = (date: any) => {
    const parsed = new Date(date);
    return date && !isNaN(parsed.getTime());
  };

  const parseDDMMYYYY = (dateStr: string) => {
    const [day, month, year] = dateStr.split("/");
    return new Date(`${year}-${month}-${day}`);
  };

  // Fetch Clients
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const param = {
          url: reqRequests.url,
          token: reqRequests.accessToken,
        };

        const result = await dispatch(allCustomersGet(param)).unwrap();

        if (result?.length > 0) {
          const options = result.map((item: any) => ({
            value: item.id.toString(),
            label: `${item.firstName} ${item.lastName}`,
            isDisabled: item.isDisabled,
          }));
          setClients([
            { value: "", label: "Please Select", isDisabled: true },
            ...options,
          ]);
        }
      } catch (err) {
        console.error("Failed to load customers", err);
      }
    };

    fetchCustomers();
  }, [customers, dispatch, reqRequests]);

  // Fetch Job if editing
  useEffect(() => {
    if (isEditMode && jobId) {
      dispatch(
        jobSingleGet({
          accessToken: localStorage.getItem("AccessToken") || "",
          url: `${protectedResources.apiTodoList.endpoint}/jobs/${jobId}`,
        })
      );
    }
  }, [isEditMode, jobId, dispatch, protectedResources.apiTodoList.endpoint]);

  // Formik Setup
  const formik = useFormik({
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,

    initialValues: {
      jobName: "",
      jobCriteria: "",
      client: { value: "", label: "" },
      startDate: null as null | Date,
      endDate: null as null | Date,
    },
    validationSchema: Yup.object({
      jobName: Yup.string().required("Please enter the job name"),
      jobCriteria: Yup.string().required("Please enter job criteria"),
      client: Yup.object()
        .shape({
          value: Yup.string().required("Please select a client"),
          label: Yup.string(),
        })
        .nullable()
        .required("Please select a client"),
      startDate: Yup.date()
        .required("Please enter the start date")
        .typeError("Invalid date"),
      endDate: Yup.date()
        .required("Please enter the end date")
        .typeError("Invalid date"),
    }),
    onSubmit: (values) => {
      if (isEditMode && jobId) {
        handleEditJobService(
          values,
          navigate,
          dispatch,
          jobRequests,
          setIsLoading,
          addAlert,
          +jobId,
          protectedResources
        );
      } else {
        handleCreateJobService(
          values,
          navigate,
          dispatch,
          jobRequests,
          setIsLoading,
          addAlert,
          protectedResources
        );
      }
    },
  });

  // Populate form if in edit mode
  useEffect(() => {
    if (selectedJob && isEditMode) {
      formik.setValues({
        jobName: selectedJob.name,
        jobCriteria: selectedJob.criteria,
        client: {
          value: selectedJob.customer.id.toString(),
          label: `${selectedJob.customer.firstName} ${selectedJob.customer.lastName}`,
        },
        startDate: isValidDate(parseDDMMYYYY(selectedJob.startDate))
          ? parseDDMMYYYY(selectedJob.startDate)
          : null,
        endDate: isValidDate(parseDDMMYYYY(selectedJob.endDate))
          ? parseDDMMYYYY(selectedJob.endDate)
          : null,
      });
      setFormReady(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedJob, isEditMode]);

  // Show loading while waiting
  if (!formReady) {
    return (
      <Container fluid className="layout-bar">
        <BreadCrumb />
        <Headline name="Loading Job..." />
        <div className="box-container">Loading job data...</div>
      </Container>
    );
  }

  return (
    <Container fluid className="layout-bar">
      <BreadCrumb />
      <Headline name={isEditMode ? "Edit Job" : "Add Job"} />

      <form onSubmit={formik.handleSubmit}>
        <div className="box-container">
          <Row>
            <Col xs={12} sm={6}>
              <LabelInsideInput
                id="jobName"
                label="Job Name"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.jobName}
                error={formik.errors.jobName}
                touched={formik.touched.jobName}
                showLabel={true}
              />
            </Col>
            <Col xs={12} sm={6}>
              <LabelInsideSelectField
                id="customer"
                name="customer"
                label="Customer"
                onChange={(selectedOption) =>
                  formik.setFieldValue("client", selectedOption)
                }
                onBlur={() => formik.setFieldTouched("client", true)}
                value={formik.values.client}
                options={clients}
                error={formik.errors}
                touched={formik.touched}
                width="w-100"
                showLabel={true}
              />
            </Col>
          </Row>
          <Row>
            <Col xs={12} sm={6}>
              <LabelInsideInput
                id="jobCriteria"
                label="Job Criteria"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.jobCriteria}
                error={formik.errors.jobCriteria}
                touched={formik.touched.jobCriteria}
                showLabel={true}
              />
            </Col>
            <Col xs={12} sm={6}>
              <LabelInsideDateInputField
                label="Start Date"
                id="startDate"
                value={formik.values.startDate}
                onChange={(date) => formik.setFieldValue("startDate", date)}
                onBlur={formik.handleBlur}
                touched={formik.touched.startDate}
                error={formik.errors.startDate}
                width="w-100"
                showLabel={true}
              />
            </Col>
          </Row>
          <Row>
            <Col xs={12} sm={6}>
              <LabelInsideDateInputField
                label="End Date"
                id="endDate"
                value={formik.values.endDate}
                onChange={(date) => formik.setFieldValue("endDate", date)}
                onBlur={formik.handleBlur}
                touched={formik.touched.endDate}
                error={formik.errors.endDate}
                width="w-100"
                showLabel={true}
              />
            </Col>
          </Row>
        </div>
        <div className="footer-btn-section d-flex justify-content-end box-container">
          <button
            className="btn btn-secondary me-3"
            type="button"
            onClick={() => navigate(-1)}
          >
            Back
          </button>
          <button className="btn btn-primary" type="submit">
            {isEditMode ? "Update Job" : "Create Job"}
          </button>
        </div>
      </form>
    </Container>
  );
};

export default JobForm;
